import { FormGroup } from '@angular/forms';

import { CropField } from '@next/arable-lib/crop-field';

import { ExternalResource } from '@next/arable-lib/external-resource';

import { ApiNotFoundErrorResponse } from '../../../shared/models';
import {
  DepthStorendeLaag,
  GrasslandOrGrassSeed,
  MainSoilTillageMethod,
  MaterialState,
  NematodeLaboraties,
  PotatoType,
  Structuurbederfsource,
} from '../../components/questionnaire/models/question-models';

export interface ProcessedAnswersWithForm {
  answers: ProcessedAnswers;
  formGroup: FormGroup;
}

export interface ProcessedAnswers {
  [key: string]: string | string[] | number | boolean | Date | { key: string; selected: boolean }[] | undefined;
}

export interface TgaState {
  cropFields: TgaCropFieldWithStatus[];
  questionnaire: TgaQuestionnaireState;
  advice: TgaAdviceState;
  heightImageData: ExternalResource<ImageWithLegendAndDescriptionResponse>;
  soilImageData: ExternalResource<ImageWithLegendResponse>;
  biomassImageData: ExternalResource<ImageWithLegendResponse>;
  biomassPotatoImageData: ExternalResource<ImageWithLegendAndDescriptionResponse>;
  satelliteImages: ExternalResource<SatelliteImage[]>;
  fieldObservationImages: ExternalResource<FieldObservationImage[]>;
  stepper: StepperState;
  varietyProperties: ExternalResource<VarietyPropertiesTGADto>;
  waterAuthority: WaterAuthorityState;
  notifications: ExternalResource<NotificationDto[]>;
  dismissedNotifications: NotificationDto[];
}

export interface WaterAuthorityState {
  exceedances: WaterAuthorityExceedanceResultDto[] | null;
  loading: boolean;
}

export interface TgaAdviceState {
  generalAdvice: AdviceStateTga | null;
  soilAdvice: AdviceStateTga | null;
  plantingAdvice: AdviceStateTga | null;
  cultivationAdvice: AdviceStateTga | null;
}

export interface StepperState {
  activeStep: TgaSteps;
}

export interface TgaQuestionnaireState {
  lastGenericStepCompleted: TgaSteps | null;
  genericQuestionnaire: {
    soilQuestions: GenericSoilQuestionsWithStatus | null;
    generalQuestions: GeneralGenericQuestionsWithStatus | null;
    plantingQuestions: GenericPlantingQuestionsWithStatus | null;
    cropYear: number | null;
  };
  specificQuestionnaire: {
    cropFieldId: number | null;
    cropYear: number | null;
    generalQuestions: GeneralQuestions | null;
    soilQuestions: SoilQuestions | null;
    plantingQuestions: PlantingQuestions | null;
  };
}
export interface TranslatedTextWithParams {
  translationKey: string;
  params?: Record<string, string>;
}
export interface FieldObservationImage {
  observationId?: number;
  description: string;
  createdAt: string;
  imageId: string;
  imageCount: number;
  imageBase64: string;
  contentType: string;
  observations?: FieldObservationSectionDto[];
}

export interface FieldObservationImagesParams {
  cropFieldId: number;
}

export interface ImageWithLegendAndDescriptionResponse extends ImageWithLegendResponse {
  description: TranslatedTextWithParams;
}

export interface ImageWithLegendResponse {
  imageData: string;
  contentType: string;
  width: number;
  height: number;
  legendData: LegendData;
}

export interface LegendData {
  entries: LegendEntry[];
}

export interface LegendEntry {
  color: string;
  label: string;
}

export interface FieldObservationImageDto {
  imageId: string;
  imageThumbnailId: string;
}

export interface TgaElevationImageParams {
  cropFieldId: number;
  width?: number;
  height?: number;
}

export interface TgaSoilImageParams {
  cropFieldId: number;
  width?: number;
  height?: number;
}

export interface TgaBiomassImageParams {
  cropFieldId: number;
  width?: number;
  height?: number;
}

export interface TgaBiomassPotatoImageParams {
  cropFieldId: number;
  width?: number;
  height?: number;
}

export interface SatelliteImageDate {
  imageDate: string;
}

export interface SatelliteImage {
  imageDate: string;
  imageBase64: string;
  contentType: string;
}

export interface FieldObservationResponseDto {
  observationId: number;
  description: string;
  images: FieldObservationImageDto[];
  imageCount: number;
  createdAt: string;
  observedThreats?: FieldObservationObservedThreatsDto;
}

export interface FieldObservationObservedThreatsDto {
  weeds: string[];
  fungi: string[];
  insects: string[];
  bacteria: string[];
  nutrientDeficiencies: string[];
  physicalDefects: string[];
  bbch?: number | null;
  cropPhase?: string | null;
  phytophthora?: string[];
}

export interface TgaSatelliteImagesParams {
  cropFieldId: number;
  fromDate?: string; // ISO 8601 format
  toDate?: string; // ISO 8601 format
}

// Notification API Models
export enum NotificationCategory {
  Warning = 'Warning',
  Message = 'Message',
}

export enum NotificationType {
  QuestionnaireNotCompleted = 'QuestionnaireNotCompleted',
  MissingCropFieldData = 'MissingCropFieldData',
  MissingNematodeAnalysis = 'MissingNematodeAnalysis',
  MissingPcnAnalysis = 'MissingPcnAnalysis',
  PlantingDateNotAdjusted = 'PlantingDateNotAdjusted',
  EmergenceDateNotAdjusted = 'EmergenceDateNotAdjusted',
  BemReportAvailable = 'BemReportAvailable',
  SatelliteImagesAvailable = 'SatelliteImagesAvailable',
  FieldObservationAdded = 'FieldObservationAdded',
  SupplementaryFertilizerAdvised = 'SupplementaryFertilizerAdvised',
  HeatstressDetectedForCustomer = 'HeatstressDetectedForCustomer',
}

export interface BaseNotificationDto {
  id?: number | null;
  type: NotificationType;
  category?: NotificationCategory;
  canBeClosed: boolean;
  isClosed: boolean;
  createdAt: Date | string;
}

export interface QuestionnaireNotCompletedNotificationDto extends BaseNotificationDto {
  type: NotificationType.QuestionnaireNotCompleted;
  missingGeneral: boolean;
  missingSoil: boolean;
  missingPlanting: boolean;
}

export interface MissingCropFieldDataCropField {
  cropFieldId: number;
  cropFieldName: string;
}

export interface MissingCropFieldDataNotificationDto extends BaseNotificationDto {
  type: NotificationType.MissingCropFieldData;
  cropFields: MissingCropFieldDataCropField[];
}

export interface MissingOrIncompleteDataCropField {
  cropFieldId: number;
  cropFieldName: string;
}

export interface HeatStressExposedCropField {
  cropFieldId: number;
  cropFieldName: string;
}

export interface MissingNematodeAnalysisNotificationDto extends BaseNotificationDto {
  type: NotificationType.MissingNematodeAnalysis;
  cropFields: MissingOrIncompleteDataCropField[];
}

export interface MissingPcnAnalysisNotificationDto extends BaseNotificationDto {
  type: NotificationType.MissingPcnAnalysis;
  cropFields: MissingOrIncompleteDataCropField[];
}

export interface PlantingDateNotAdjustedNotificationDto extends BaseNotificationDto {
  type: NotificationType.PlantingDateNotAdjusted;
  cropFields: MissingOrIncompleteDataCropField[];
}

export interface EmergenceDateNotAdjustedNotificationDto extends BaseNotificationDto {
  type: NotificationType.EmergenceDateNotAdjusted;
  cropFields: MissingOrIncompleteDataCropField[];
}

export interface BemReportAvailableNotificationDto extends BaseNotificationDto {
  type: NotificationType.BemReportAvailable;
  cropFieldName: string;
  cropFieldId: number;
}

export interface SatelliteImagesAvailableNotificationDto extends BaseNotificationDto {
  type: NotificationType.SatelliteImagesAvailable;
  cropFieldName: string;
  cropFieldId: number;
}

export interface FieldObservationImageDialogDto {
  imageUrl?: string;
  thumbnailUrl?: string;
  imageBase64?: string;
  thumbnailBase64?: string;
  contentType?: string;
  alt?: string;
}

export interface FieldObservationSectionDto {
  category: string;
  items: string[];
}

export interface FieldObservationDialogDto {
  observationDate?: string;
  cropFieldName?: string;
  remarks?: string;
  images?: FieldObservationImageDialogDto[];
  observations?: FieldObservationSectionDto[];
}

export interface FieldObservationAddedNotificationDto extends BaseNotificationDto {
  type: NotificationType.FieldObservationAdded;
  cropFieldName: string;
  cropFieldId: number;
}

export interface SupplementaryFertilizerAdvisedNotificationDto extends BaseNotificationDto {
  type: NotificationType.SupplementaryFertilizerAdvised;
  cropFieldName: string;
  cropFieldId: number;
}

export interface HeatstressDetectedForCustomerNotificationDto extends BaseNotificationDto {
  type: NotificationType.HeatstressDetectedForCustomer;
  cropFieldNames: string[];
  fromDate: string;
  toDate: string;
}

export type NotificationDto =
  | QuestionnaireNotCompletedNotificationDto
  | MissingCropFieldDataNotificationDto
  | MissingNematodeAnalysisNotificationDto
  | MissingPcnAnalysisNotificationDto
  | PlantingDateNotAdjustedNotificationDto
  | EmergenceDateNotAdjustedNotificationDto
  | BemReportAvailableNotificationDto
  | SatelliteImagesAvailableNotificationDto
  | FieldObservationAddedNotificationDto
  | SupplementaryFertilizerAdvisedNotificationDto
  | HeatstressDetectedForCustomerNotificationDto;

// Type predicate functions for notification type narrowing
export function hasDateAndCropField(
  notification: NotificationDto,
): notification is
  | BemReportAvailableNotificationDto
  | SatelliteImagesAvailableNotificationDto
  | FieldObservationAddedNotificationDto {
  return 'cropFieldName' in notification;
}

export function hasCropFieldsArray(
  notification: NotificationDto,
): notification is
  | MissingCropFieldDataNotificationDto
  | MissingNematodeAnalysisNotificationDto
  | MissingPcnAnalysisNotificationDto
  | PlantingDateNotAdjustedNotificationDto
  | EmergenceDateNotAdjustedNotificationDto {
  return 'cropFields' in notification;
}

export enum StepStatus {
  Inactive = 'inactive',
  Active = 'active',
  Completed = 'completed',
}

export interface CropCycleStep {
  translationKey: TgaSteps;
  status: StepStatus;
  enabled?: boolean;
  special?: boolean;
}

export interface CropCyclePhase {
  translationKey: string;
  steps: CropCycleStep[];
}

export interface GeneralQuestions {
  targetPurpose: PotatoType;
  targetExport: boolean;
  lastHarvestedYear?: number | null;
}

export interface GeneralGenericQuestionsWithStatus {
  hasFilledInSpecific: boolean;
  genericGeneral: GenericGeneralQuestions | null;
}

export interface GenericGeneralQuestions {
  targetPurpose: PotatoType;
  targetExport: boolean;
}

export interface GeneralQuestionsRequest extends GeneralQuestions {
  cropFieldId: number;
  cropYear: number;
  cropVarietyId?: number | null;
}
export interface GenericGeneralQuestionsRequest extends GenericGeneralQuestions {
  cropYear: number;
}

export interface AdviceResponse {
  subjectTransCode: string;
  riskOfDamage: string;
  necessityForAction: string;
  measures: MeasureResponse[];
  adviceItems: AdviceItemResponse[] | null;
  soilVarietyDifferences?: string;
}

export interface AdviceWithParamsResponse {
  adviceLines: AdviceResponse[];
  translationParams: Record<string, string>;
}

export type TranslationParams = Record<string, string | Record<string, unknown>>;

export interface MeasureResponse {
  type: string;
  translationCode: string;
  isSummation?: boolean | null;
  issueType?: IssueType;
  generic?: boolean;
}

export interface AdviceItemResponse {
  translationCode: string;
  isSummation?: boolean | null;
  issueType?: IssueType;
  generic?: boolean;
}

export interface DisplayAdvice {
  subjectTransCode: string;
  riskOfDamage: RiskLevel;
  necessityForAction: ActionNecessity;
  issueType: IssueType;
  measure?: MeasureResponse[];
  adviceItem?: AdviceItemResponse[] | null;
  soilVarietyDifferences?: string;
}

export interface AdviceStateTga {
  data: DisplayAdvice[] | null;
  cropFieldId: number | null;
  cropYear: number | null;
  translationParams: TranslationParams | null;
}

export enum IssueType {
  Phytophthora = 'Phytophthora',
  GewoneSchurft = 'Gewone Schurft',
  Ritnaalden = 'Ritnaalden',
  Knolzetting = 'Knolzetting',
  Aaltjes = 'Aaltjes',
  Aardappelopslag = 'Aardappelopslag',
  Aardappelmoeheid = 'Aardappelmoeheid',
  CEC = 'CEC',
  Fusarium = 'Fusarium',
  Sclerotinia = 'Sclerotinia',
  Structuurbederf = 'Structuurbederf',
  ZwarteSpikkel = 'Zwarte Spikkel',
  Bodemmonster = 'Bodemmonster',
  ZilverSchurft = 'Zilver Schurft',
  Stuifgevoeligheid = 'Stuifgevoeligheid',
  SoilVariety = 'Bodemvariatie',
  RhizoctoniaGrondbehandeling = 'Rhizoctonia Grondbehandeling',
  RhizoctoniaKnolbehandeling = 'Rhizoctonia Knolbehandeling',
  PH = 'pH',
  AlgemeenAdvies = 'Algemeen Advies',
  Granulaten = 'Granulaten',
  Slakken = 'Slakken',
}

export enum RiskLevel {
  No = 'No',
  Present = 'Present',
  Likely = 'Likely',
  VeryLikely = 'VeryLikely',
  High = 'High',
  Unknown = 'Unknown',
}

export enum ActionNecessity {
  No = 'No',
  Present = 'Present',
  Desired = 'Desired',
  VeryDesired = 'VeryDesired',
  High = 'High',
  Unknown = 'Unknown',
}

export type GetTgaSoilImageResponse = ImageWithLegendResponse | ApiNotFoundErrorResponse;
export type GetTgaBiomassImageResponse = ImageWithLegendResponse | ApiNotFoundErrorResponse;
export type GetTgaBiomassPotatoImageResponse = ImageWithLegendAndDescriptionResponse | ApiNotFoundErrorResponse;
export type GetTgaVarietyPropertiesResponse = VarietyPropertiesTGADto | ApiNotFoundErrorResponse;
export type GetTgaHeightImageResponse = ImageWithLegendAndDescriptionResponse | ApiNotFoundErrorResponse;
export type GetNotificationsResponse = NotificationDto[] | ApiNotFoundErrorResponse;

export interface DiseasesOnPlotResult {
  hadAaltjesDamage: boolean;
  expectedStorageOnCropField: boolean;
  hasGewoneSchurftPressure: boolean;
  hasRhizoctoniaPressure: boolean;
  hadRitnaalden: boolean;
  hasSclerotiniaPressure: boolean;
  hadTabaksratelvirus: boolean;
  hasZwarteSpikkelPressure: boolean;
  hasSnails: boolean;
}

export interface WaterAuthorityExceedance {
  substance: string;
  cropAid: string;
}

export interface WaterAuthorityExceedanceResultDto {
  waterBoardName: string;
  exceedances: WaterAuthorityExceedance[];
}

export interface BiomassBenchmarkPointDto {
  dateUtc: string;
  value: number | null;
}

export interface BiomassBenchmarkEvaluationCropFieldRequest {
  cropFieldId: number;
  cropVarietyId: number;
}

export interface BiomassBenchmarkComparableCropFieldDto {
  points: BiomassBenchmarkPointDto[];
}

export interface BiomassBenchmarkResponseDto {
  points: BiomassBenchmarkPointDto[];
  comparableCropFields: BiomassBenchmarkComparableCropFieldDto[];
}

export interface BiomassBenchmarkEvaluationCropFieldDto {
  cropFieldId: string;
  cropVarietyId: number;
  points: BiomassBenchmarkPointDto[];
}

export interface BiomassBenchmarkEvaluationBenchmarkDto {
  cropVarietyId: number;
  points: BiomassBenchmarkPointDto[];
}

export interface BiomassBenchmarkEvaluationResponseDto {
  cropFields: BiomassBenchmarkEvaluationCropFieldDto[];
  benchmarks: BiomassBenchmarkEvaluationBenchmarkDto[];
  cropVarietyIds: number[];
}

export interface GenericSoilQuestionsWithStatus {
  hasFilledInSpecific: boolean;
  genericSoil: GenericSoilQuestions | null;
}

export interface GenericSoilQuestions {
  lastHarvestedYear: number | null;
  mainSoilTillageMethod: MainSoilTillageMethod | null;
  hasIrrigation: boolean | null;
  isInLuwte: boolean | null;
  greenManureCropId: number | null;

  // field sensitivity
  hasZwarteSpikkelPressure: boolean | null;
  hasSclerotiniaPressure: boolean | null;
  hasRhizoctoniaPressure: boolean | null;
  hadRitnaalden: boolean | null;
  hasGewoneSchurftPressure: boolean | null;
  hadGrasslandOrGrassSeed: GrasslandOrGrassSeed | null;
  expectedStorageOnCropField: boolean | null;
  hadAaltjesDamage: boolean | null;
  hadTabaksratelvirus: boolean | null;
  hasSnails: boolean | null;

  // soil variety
  hasSoilVariety: boolean | null;
  soilVarietyDifferences: string | null;
  isStuifgevoelig: boolean | null;
  hasSoilScan: boolean | null;

  // structure
  structuurbederfSource: Structuurbederfsource | null;
  hasProefkuil: boolean | null;
  depthStorendeLaag: DepthStorendeLaag | null;
  isBeredenUnderWetConditions: boolean | null;
  hasWorkingDrainage: boolean | null;
  hasLaagten: boolean | null;

  // uitgangsmateriaal
  knolbehandelingMaterial: MaterialState | null;
  isRhizoctoniaExpected: boolean | null;
  isCropVarietyRhizoctoniaSensitive: boolean | null;
  hasZilverschurftdrukInUitgangsmateriaal: boolean | null;
  isFusariumExpected: boolean | null;

  // vrijlevend analysis
  hasAManalysis: boolean | null;

  // Aardappelmoeheid (AM) analysis
  hasAaltjesAnalysis: boolean | null;

  // unused field
  cropRotation: Array<string> | null;
  totalYearsWithGrassGrainCorn: number | null;
  totalYearsWithGrassGrainCorn_IsManual: boolean | null;
}

export interface SoilQuestions {
  isRentedField: boolean;
  lastHarvestedYear: number | null;
  mainSoilTillageMethod: MainSoilTillageMethod | null;
  hasIrrigation: boolean | null;
  isInLuwte: boolean | null;
  greenManureCropId: number | null;

  // field sensitivity
  hasZwarteSpikkelPressure: boolean | null;
  hasSclerotiniaPressure: boolean | null;
  hasRhizoctoniaPressure: boolean | null;
  hadRitnaalden: boolean | null;
  hasGewoneSchurftPressure: boolean | null;
  hadGrasslandOrGrassSeed: GrasslandOrGrassSeed | null;
  expectedStorageOnCropField: boolean | null;
  hadAaltjesDamage: boolean | null;
  hadTabaksratelvirus: boolean | null;
  hasSnails: boolean | null;

  // soil variety
  hasSoilVariety: boolean | null;
  soilVarietyDifferences: string | null;
  isStuifgevoelig: boolean | null;
  hasSoilScan: boolean | null;

  // structure
  structuurbederfSource: Structuurbederfsource | null;
  hasProefkuil: boolean | null;
  depthStorendeLaag: DepthStorendeLaag | null;
  isBeredenUnderWetConditions: boolean | null;
  hasWorkingDrainage: boolean | null;
  hasLaagten: boolean | null;

  // uitgangsmateriaal
  knolbehandelingMaterial: MaterialState | null;
  isRhizoctoniaExpected: boolean | null;
  isCropVarietyRhizoctoniaSensitive: boolean | null;
  hasZilverschurftdrukInUitgangsmateriaal: boolean | null;
  isFusariumExpected: boolean | null;

  // vrijlevend analysis
  vrijlevendAnalysisHasAaltjesAnalysis: boolean | null;
  vrijlevendAnalysisSampleYear: number | null;
  vrijlevendAnalysisDitylenchusDestructor: number | null;
  vrijlevendAnalysisDitylenchusDipsaci: number | null;
  vrijlevendAnalysisParatrichodorusPachydermus: number | null;
  vrijlevendAnalysisParatrichodorusTeres: number | null;
  vrijlevendAnalysisTrichodorusPrimitivus: number | null;
  vrijlevendAnalysisTrichodorusSimilis: number | null;
  vrijlevendAnalysisMeloidogyneChitwoodi: number | null;
  vrijlevendAnalysisMeloidogyneFallax: number | null;
  vrijlevendAnalysisMeloidogyneHapla: number | null;
  vrijlevendAnalysisPratylenchusPenetrans: number | null;

  // Aardappelmoeheid (AM) analysis
  amAnalysisHasAmAnalysis: boolean | null;
  amAnalysisAmSampleYear: number | null;
  amAnalysisLaboratory: NematodeLaboraties | null;
  amAnalysisTotalLevendeLarvenEieren: number | null;
  amAnalysisGRostochiensisPercentage: number | null;
  amAnalysisGPallidaPercentage: number | null;

  // unused field
  cropRotation: Array<string> | null;
  totalYearsWithGrassGrainCorn: number | null;
  totalYearsWithGrassGrainCorn_IsManual: boolean | null;
}

export interface GenericSoilQuestionsRequest extends GenericSoilQuestions {
  cropYear: number;
}

export interface SoilQuestionsRequest extends SoilQuestions {
  cropFieldId: number;
  cropYear: number;
}

export interface GenericPlantingQuestionsWithStatus {
  hasFilledInSpecific: boolean;
  genericPlanting: GenericPlantingQuestions | null;
}

export interface GenericPlantingQuestions {
  hasKnolzettingStengelaantalIssue: boolean | null;
  hasUitgroeiKnollenIssue: boolean | null;
  hasNEfficiencyIssue: boolean | null;
  hasTemperatuurDroogtestressIssue: boolean | null;
  longStorageExpected: boolean | null;
}

export interface GenericPlantingQuestionsRequest extends GenericPlantingQuestions {
  cropYear: number;
}

export interface PlantingQuestions {
  hasKnolzettingStengelaantalIssue: boolean | null;
  hasUitgroeiKnollenIssue: boolean | null;
  hasNEfficiencyIssue: boolean | null;
  hasTemperatuurDroogtestressIssue: boolean | null;
  longStorageExpected: boolean | null;
}

export interface PlantingQuestionsRequest extends PlantingQuestions {
  cropFieldId: number;
  cropYear: number;
}

export interface QuestionnaireAnswer {
  key: string;
  value: QuestionnaireAnswerValue;
}

export type QuestionnaireAnswerValue =
  | string
  | number
  | boolean
  | string[]
  | Date
  | { key: string; selected: boolean }[]
  | undefined;
export interface VarietyPropertiesTGADto {
  id: number;
  ediCode: number;
  cropVarietyName: string;
  productionPurposeCategoryId: number;
  productionPurposeCategoryName: string;
  knolZetting: number | null;
  vroegheidRas: number | null;
  rasScorePhytophthora: number | null;
  rasScoreSchurft: number | null;
  rasScoreZwarteSpikkel: number | null;
  tradingCompany: string | null;
  nvwaGloboderaRos14Score: number | null;
  tradingGloboderaRos14Score: number | null;
  nvwaGloboderaRos14Rv: number | null;
  nvwaGloboderaRos23Score: number | null;
  tradingGloboderaRos23Score: number | null;
  nvwaGloboderaRos23Rv: number | null;
  nvwaGloboderaPal2Score: number | null;
  tradingGloboderaPal2Score: number | null;
  nvwaGloboderaPal2Rv: number | null;
  nvwaGloboderaPal3Score: number | null;
  tradingGloboderaPal3Score: number | null;
  nvwaGloboderaPal3Rv: number | null;
  nvwaGloboderaRos5Score: number | null;
  tradingGloboderaRos5Score: number | null;
  nvwaGloboderaRos5Rv: number | null;
  scoreAlternaria: number | null;
  scoreFusarium: number | null;
  scorePhytophthoraTuber: number | null;
  scoreLeafrollVirus: number | null;
  scoreVirusYntnTuber: number | null;
  nvwaWartDiseaseF1Score: number | null;
  nvwaWartDiseaseF26Score: number | null;
  nvwaWartDiseaseF18Score: number | null;
}

export enum PotatoDiseases {
  Rhizoctonia = 'Rhizoctonia',
  Zilverschurft = 'Zilverschurft',
  Fusarium = 'Fusarium',
}

export interface TgaCropFieldWithStatus {
  cropField: CropField;
  questionnaireStatus: TgaQuestionnaireStatus;
}

export interface TgaQuestionnaireStatus {
  recommendedStartingStep: TgaSteps;
  lastSpecificStepCompleted: TgaSteps | null;
  lastGenericStepCompleted: TgaSteps | null;
}

export enum TgaSteps {
  General = 'General',
  Soil = 'Soil',
  Planting = 'Planting',
  Growing = 'Growing',
  Evaluation = 'Evaluation',
}

export const VALID_STEP_NAMES = Object.values(TgaSteps) as ReadonlyArray<TgaSteps>;

export type ObservedThreatCategoryKey =
  | 'weeds'
  | 'fungi'
  | 'insects'
  | 'bacteria'
  | 'nutrientDeficiencies'
  | 'physicalDefects';

export const ObservedThreatCategories: Array<{
  key: ObservedThreatCategoryKey;
  categoryTranslationKey: string;
  itemTranslationPrefix: string;
}> = [
  {
    key: 'weeds',
    categoryTranslationKey: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.Categories.Weeds',
    itemTranslationPrefix: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.Weeds',
  },
  {
    key: 'fungi',
    categoryTranslationKey: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.Categories.Fungi',
    itemTranslationPrefix: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.Fungi',
  },
  {
    key: 'insects',
    categoryTranslationKey: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.Categories.Insects',
    itemTranslationPrefix: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.Insects',
  },
  {
    key: 'bacteria',
    categoryTranslationKey: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.Categories.Bacteria',
    itemTranslationPrefix: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.Bacteria',
  },
  {
    key: 'nutrientDeficiencies',
    categoryTranslationKey: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.Categories.NutrientDeficiencies',
    itemTranslationPrefix: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.NutrientDeficiencies',
  },
  {
    key: 'physicalDefects',
    categoryTranslationKey: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.Categories.PhysicalDefects',
    itemTranslationPrefix: 'TGA.Notifications.FieldObservationDialog.ObservedThreats.PhysicalDefects',
  },
];
